import { useState } from "react";
import { Button } from "@common/Button";
import { Icon } from "@common/index";
import { ComponentColor } from "@common/types";
import { optionNamed, stageValue } from "../_helpers/variantSelection";

// CreateOptionValidator's limits (DomainConstraint.Option.Name.MaxLength and
// DomainConstraint.OptionValue.Value.MaxLength).
const NAME_MAX_LENGTH = 64;
const VALUE_MAX_LENGTH = 128;

interface AddOptionRowProps {
    /** The offered options' names: a new option may not repeat one. */
    existingNames: string[];
    isAdding: boolean;
    addFailed: boolean;
    /** Resolves true once the option is created; the row then clears. */
    onAdd: (name: string, values: string[]) => Promise<boolean>;
}

// "Add New Option": a name, its values staged as chips, and one Add that
// creates the option with all of them. A failed Add keeps what was typed.
export const AddOptionRow = ({
    existingNames,
    isAdding,
    addFailed,
    onAdd,
}: AddOptionRowProps) => {
    const [name, setName] = useState("");
    const [values, setValues] = useState<string[]>([]);
    const [typed, setTyped] = useState("");

    // A value still in the input counts: Add takes it too rather than drop it.
    const allValues = stageValue(values, typed);
    const takenName = optionNamed(existingNames, name);
    const canAdd =
        name.trim() !== "" && !takenName && allValues.length > 0;

    const stageTyped = () => {
        setValues(allValues);
        setTyped("");
    };

    const handleAdd = async () => {
        if (await onAdd(name.trim(), allValues)) {
            setName("");
            setValues([]);
            setTyped("");
        }
    };

    return (
        <div role="group" aria-label="Add New Option" className="mt-4">
            <p className="mb-1 text-neutral">Add New Option</p>
            <div className="flex items-start gap-3">
                {/* Marked so focus returns here, not to Close, once a create
                    settles: the next Enter must not close the picker. */}
                <input
                    aria-label="New option name"
                    aria-describedby={takenName ? "new-option-name-taken" : undefined}
                    aria-invalid={takenName ? true : undefined}
                    data-dialog-initial-focus
                    className="input input-bordered w-48 text-neutral"
                    placeholder="Name"
                    maxLength={NAME_MAX_LENGTH}
                    value={name}
                    disabled={isAdding}
                    onChange={(e) => setName(e.target.value)}
                />
                <div className="input input-bordered flex h-auto min-h-10 flex-1 flex-wrap items-center gap-1 py-1 text-neutral">
                    <button
                        type="button"
                        aria-label="Add value"
                        className="btn btn-ghost btn-xs btn-circle text-primary"
                        disabled={isAdding}
                        onClick={stageTyped}
                    >
                        <Icon name="add_circle" className="text-xl" />
                    </button>
                    <ul aria-label="New option values" className="flex flex-wrap gap-1">
                        {values.map((value) => (
                            <li
                                key={value}
                                className="badge badge-primary gap-1"
                            >
                                {value}
                                <button
                                    type="button"
                                    aria-label={`Remove ${value}`}
                                    disabled={isAdding}
                                    onClick={() =>
                                        setValues(values.filter((v) => v !== value))
                                    }
                                >
                                    <Icon name="close" className="text-sm" />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <input
                        aria-label="New option value"
                        className="min-w-24 flex-1 outline-none"
                        maxLength={VALUE_MAX_LENGTH}
                        value={typed}
                        disabled={isAdding}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") stageTyped();
                        }}
                    />
                </div>
                <Button
                    color={ComponentColor.Primary}
                    loading={isAdding}
                    disabled={!canAdd}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </div>
            {takenName && (
                <p id="new-option-name-taken" className="mt-2 text-sm text-error">
                    {`There is already an option called ${takenName}.`}
                </p>
            )}
            {addFailed && (
                <p role="alert" className="mt-2 text-sm text-error">
                    Couldn't add the option. Try again.
                </p>
            )}
        </div>
    );
};
